import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import Slideshow from '../Shared/Slideshow/Slideshow';
import Link from 'next/link';

import useWindowResize from 'pubweb-smokey/dist/hooks/useWindowResize';
import HomeCardAspect from '@components/Shared/HomeCards/Standard/HomeCardAspect';
import * as variables from 'pubweb-smokey/dist/components/GridSystem/_vars_widths.js';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';

import { getModelsByModelNumbers } from '@services/homesService';
import { LocationsContext } from '@contexts/LocationsContext';
import { DXFavoritesContext } from '@contexts/DXFavoritesContext';
import { getSiteUrl } from '@utils/config';

import { HomeStreamStyles } from './HomeStream.styled';

const HomeStream = ({ stream, internallyLinked = true }) => {
  const locationsContext = useContext(LocationsContext);
  const favoritesContext = useContext(DXFavoritesContext);

  const [modelsViewable, setModelsViewable] = useState(3);
  const [curatedHomes, setCuratedHomes] = useState([]);
  const [selectedCard, setSelectedCard] = useState(0);

  useWindowResize(() => {
    if (window.innerWidth >= 960) {
      setModelsViewable(3);
      setSelectedCard(0);
    } else if (window.innerWidth >= variables.desktop_breakpoint) {
      setModelsViewable(2);
      setSelectedCard(0);
    } else {
      setModelsViewable(1);
      setSelectedCard(0);
    }
  }, [960, variables.desktop_breakpoint]);

  useEffect(() => {
    if (locationsContext.state.geocode) {
      getModelsByModelNumbers(
        stream.curatedHomes.map((c) => c.modelNumber),
        locationsContext.state.geocode.latitude,
        locationsContext.state.geocode.longitude,
        locationsContext.state.distance
      ).then((results) => {
        for (let i = 0; i < stream.curatedHomes.length; i++) {
          const home = stream.curatedHomes[i];
          // eslint-disable-next-line prefer-destructuring
          const model = results.filter(
            (m) =>
              m.modelNumber.toLowerCase().trim() ==
              home.modelNumber.toLowerCase().trim()
          )[0];
          home.model = model && model.dealers.length > 0 ? model : null;
        }

        setCuratedHomes(stream.curatedHomes.filter((home) => home.model));
        setSelectedCard(0);
      });
    }
  }, [locationsContext.state.geocode, locationsContext.state.distance]);

  const getSliderViewPercentage = () => {
    switch (modelsViewable) {
      case 1:
        return 100;

      case 2:
        return 50;

      case 3:
        return 33.3;

      default:
        return undefined;
    }
  };

  const getSliderButtonState = () => {
    if (modelsViewable >= curatedHomes.length) {
      return 'hide-next hide-prev';
    }

    if (selectedCard === 0) {
      return 'hide-prev';
    }

    if (selectedCard === curatedHomes.length - modelsViewable) {
      return 'hide-next';
    }
  };

  return (
    curatedHomes.length > 0 && (
      <HomeStreamStyles className={getSliderButtonState()}>
        <div className="container">
          {internallyLinked ? (
            <Link href={stream.slug} className="header-link" to={stream.slug}>
              <h4>{stream.collectionTitle}</h4>
            </Link>
          ) : (
            <a className="header-link" href={stream.slug}>
              <h4>{stream.collectionTitle}</h4>
            </a>
          )}
          <Slideshow
            selectedItem={selectedCard}
            onChange={(cardIndex) => {
              setSelectedCard(cardIndex);
            }}
            onClickItem={() => {
              setSelectedCard();
            }}
            showThumbs={false}
            centerMode
            centerSlidePercentage={getSliderViewPercentage()}
            imageArray={curatedHomes.map((home) => {
              const { model } = home;
              const cardLink = internallyLinked
                ? `/homes/${model.modelNumber}/`
                : getSiteUrl(`/homes/${model.modelNumber}/`);

              return (
                <HomeCardAspect
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  showPrice={stream.displayHomePrice}
                  key={`$home-card-${model.modelNumber}`}
                  homeObject={{
                    ...model,
                    altTextDescription:
                      home.cardImage.description || model.modelDescription,
                  }}
                  imageAspectRatio={{ width: 512, height: 365 }}
                  images={[{ reference: home.cardImage.url }]}
                  cardLink={cardLink}
                  useSlideshow={false}
                  lowPrice={model.lowPrice}
                  isInStock={
                    model.dealers.filter((d) => d.hasModelInStock).length > 0
                  }
                  showFavoritingHeart={true}
                  dynamicHeight={false}
                  showStockIndicator={true}
                  imageQueryString={`?w=512&fm=webp`}
                  isFavorited={
                    favoritesContext.state.favoriteModels?.filter(
                      (faveModels) =>
                        faveModels.modelNumber === home.modelNumber
                    )[0]
                  }
                  handleFavoriteClick={(e) => {
                    e.preventDefault();
                    favoritesContext.actions.toggleFavoriteModel(
                      model.modelNumber
                    );
                    pushGTMEvent('clickFavoritesHeart');
                  }}
                />
              );
            })}
          ></Slideshow>
        </div>
      </HomeStreamStyles>
    )
  );
};

HomeStream.propTypes = {
  internallyLinked: PropTypes.bool,
  stream: PropTypes.shape({
    collectionTitle: PropTypes.any,
    curatedHomes: PropTypes.array,
    displayHomePrice: PropTypes.any,
    slug: PropTypes.any,
  }),
};

export default HomeStream;
